import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Toolbar, AppBar, Typography, IconButton, Avatar, Menu, MenuItem,
  Divider, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  useTheme, useMediaQuery,
} from '@mui/material';
import { CalendarMonth, EventBusy, Dashboard, Logout, Menu as MenuIcon, Settings } from '@mui/icons-material';
import { useState } from 'react';
import { useAuthStore } from '@/store/authStore';
import { useLogout } from '@/hooks/useLogout';
import { BrandingFooter } from '@/components/BrandingFooter';
import { useSessionExpiry } from '@/hooks/useSessionExpiry';

const DRAWER_WIDTH = 220;

const navItems = [
  { label: 'Today', path: '/teacher', icon: <Dashboard /> },
  { label: 'My Timetable', path: '/teacher/timetable', icon: <CalendarMonth /> },
  { label: 'My Absences', path: '/teacher/absences', icon: <EventBusy /> },
  { label: 'Settings', path: '/teacher/settings', icon: <Settings /> },
];

export function TeacherLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const user = useAuthStore((state) => state.user);
  const logoutMutation = useLogout();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useSessionExpiry();

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarMonth color="primary" />
          <Typography variant="h6" noWrap>Timetable</Typography>
        </Box>
      </Toolbar>
      <Divider />
      <Box sx={{ px: 2, pt: 2, pb: 0.5 }}>
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>Teacher Portal</Typography>
      </Box>
      <List sx={{ flex: 1 }}>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => { navigate(item.path); if (isMobile) setDrawerOpen(false); }}
              sx={{ mx: 1, mb: 0.5, borderRadius: 2.5 }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Avatar sx={{ width: 34, height: 34 }}>
          {user?.name?.[0]?.toUpperCase() ?? 'T'}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{user?.name ?? 'Teacher'}</Typography>
          <Typography variant="caption" color="text.secondary">Teacher</Typography>
        </Box>
      </Box>
      <BrandingFooter variant="compact" />
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* AppBar */}
      <AppBar position="fixed" color="default" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" sx={{ mr: 2 }} onClick={() => setDrawerOpen((o) => !o)}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
            {navItems.find((n) => n.path === location.pathname)?.label ?? 'Teacher Portal'}
          </Typography>
          <IconButton color="inherit" onClick={(e) => setAnchorEl(e.currentTarget)}>
            <Avatar sx={{ width: 32, height: 32 }}>
              {user?.name?.[0]?.toUpperCase() ?? 'T'}
            </Avatar>
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem disabled>
              <Box>
                <Typography variant="body2">{user?.name}</Typography>
                <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setAnchorEl(null); logoutMutation.mutate(); }}>
              <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar — persistent on desktop, temporary on mobile */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={isMobile ? drawerOpen : true}
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          ml: isMobile ? 0 : `${DRAWER_WIDTH}px`,
          height: '100vh',
          bgcolor: 'background.default',
          overflow: 'hidden',
        }}
      >
        <Toolbar />
        <Box sx={{ flexGrow: 1, p: 3, overflow: 'auto' }}>
          <Outlet />
        </Box>
        <BrandingFooter />
      </Box>
    </Box>
  );
}
